import { InlineBanner } from "components/ui/inline-banner";
import React from "react";

interface MapErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

interface MapErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

let webglSupportCache: boolean | null = null;

export const isWebGLSupported = (): boolean => {
    if (webglSupportCache !== null) {
        return webglSupportCache;
    }

    if (!window.WebGLRenderingContext) {
        webglSupportCache = false;
        return false;
    }

    const canvas = document.createElement("canvas");
    let context: WebGLRenderingContext | WebGL2RenderingContext | null = null;

    try {
        context = canvas.getContext("webgl2") || canvas.getContext("webgl");
        if (context && typeof context.getParameter === "function") {
            webglSupportCache = true;
            return true;
        }
    } catch (e) {
        webglSupportCache = false;
        return false;
    } finally {
        if (context) {
            try {
                const ext = context.getExtension("WEBGL_lose_context");
                if (ext) {
                    ext.loseContext();
                }
            } catch (e) {
                // Extension might not be available, ignore
            }
        }
        // Remove canvas from DOM to help with cleanup
        canvas.remove();
    }

    webglSupportCache = false;
    return false;
};

const isWebGLError = (error?: Error): boolean => {
    if (!error?.message) return false;

    return (
        error.message.includes("Could not compile fragment shader") ||
        error.message.includes("Could not compile vertex shader") ||
        error.message.includes("WebGL") ||
        error.message.includes("shader") ||
        error.message.includes("precision is not supported") ||
        error.message.includes("Program failed to link")
    );
};

export class MapErrorBoundary extends React.Component<
    MapErrorBoundaryProps,
    MapErrorBoundaryState
> {
    constructor(props: MapErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): MapErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        if (isWebGLError(error)) {
            console.warn("Map WebGL error caught by error boundary:", error.message);
        } else {
            console.error("Map error caught by error boundary:", error, errorInfo);
        }
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            if (isWebGLError(this.state.error)) {
                return (
                    <InlineBanner type="info">
                        Map display is not available on this device. This may be due to
                        limited graphics support or disabled WebGL. The search results are
                        still available in the list view.
                    </InlineBanner>
                );
            }

            return (
                <InlineBanner type="error">
                    Map display is temporarily unavailable. Please try refreshing the
                    page.
                </InlineBanner>
            );
        }

        if (!isWebGLSupported()) {
            return (
                <InlineBanner type="info">
                    Map display is not available on this device due to missing WebGL
                    support. The search results are still available in the list view.
                </InlineBanner>
            );
        }

        return this.props.children;
    }
}
